import { Grid } from '../../core/Grid.js';
import { splitEntries } from '../stateSerialization.js';
import {
    Position,
    Direction,
    positionKey,
    isInside,
    turnCW,
    turnCCW,
    oppositeDir
} from '../../core/GridGeometry.js';
import { ScoreBreakdown, calculateScore } from '../../core/Scoring.js';
import type { ScoringHighlight } from '../scoringRules.js';

/** A turn arrow: clockwise or counter-clockwise 90° rotation of the cart's heading. */
export type TurnType = 'CW' | 'CCW';

/** One occupancy of a cell along the drive: the cart is at `pos`, about to leave in `dir`. */
export interface CartStep {
    pos: Position;
    dir: Direction;      // outgoing heading, after any turn + wall bounce
    uturn: boolean;      // this step bounced off a wall
    teleport?: boolean;  // arrived here via a Vortex gate (base game never sets this)
}

export interface CartPath {
    steps: CartStep[];
    /** The drive returned cleanly to the start state (top-left, heading E). */
    loopClosed: boolean;
    /** Digits in the order they were first driven over (for status messages). */
    digitsHit: number[];
    /** Pass count per digit over one loop period. */
    passCounts: Map<number, number>;
    /** Digits in the order they reach their second pass (their clear order). */
    clearOrder: number[];
    /** Digits driven over three or more times. */
    overPassed: number[];
}

/**
 * Turns — a cart rolls from the top-left, turning at player-placed arrows and U-turning at
 * the boundary, always looping back to the start. Each digit must be driven over exactly
 * twice; the second pass clears it, and clears must happen in digit order.
 *
 * The cart's outgoing direction at a cell is `resolveOut(cell, incoming)`: apply the turn
 * (if any), then a wall U-turn (if the turned heading leaves the grid). State is
 * `(cell, outgoing dir)`, a finite set, so the drive is eventually periodic; a clean board
 * returns to the start state and every used cell is driven an even number of times.
 */
export class TurnsGame {
    protected grid: Grid;
    protected turns: Map<string, TurnType> = new Map();
    protected cachedPath: CartPath | null = null;

    protected get gs() { return this.grid.serializer; }

    /** Fixed cart start: top-left corner, heading east. */
    static readonly START: Position = { row: 0, col: 0 };
    static readonly START_DIR: Direction = 'E';

    constructor(grid: Grid) {
        this.grid = grid;
    }

    getGrid(): Grid {
        return this.grid;
    }

    getTurns(): Map<string, TurnType> {
        return new Map(this.turns);
    }

    // ── Path highlight selection (transient view state, like Ignition) ──

    protected selectedDigit: number | null = null;
    protected selectedStart = false;

    /** Toggle a digit's outgoing-path highlight (clears any start highlight). */
    toggleSelectedDigit(digit: number): void {
        this.selectedDigit = this.selectedDigit === digit ? null : digit;
        this.selectedStart = false;
    }

    /** Toggle the start terminal's initial-path highlight (clears any digit highlight). */
    toggleSelectedStart(): void {
        this.selectedStart = !this.selectedStart;
        this.selectedDigit = null;
    }

    getSelectedDigit(): number | null {
        return this.selectedDigit;
    }

    isStartSelected(): boolean {
        return this.selectedStart;
    }

    getTurnAt(pos: Position): TurnType | null {
        return this.turns.get(positionKey(pos)) ?? null;
    }

    isStarted(): boolean {
        return this.turns.size > 0;
    }

    setTurn(pos: Position, turn: TurnType | null): void {
        // A turn may sit on the start corner (it just bends the cart's first step); only
        // digit cells are off-limits.
        if (this.grid.hasDigitAt(pos)) return;

        const key = positionKey(pos);
        if (turn === null) {
            this.turns.delete(key);
        } else {
            this.turns.set(key, turn);
        }
        this.cachedPath = null;
    }

    cycleTurn(pos: Position): void {
        const current = this.getTurnAt(pos);
        if (current === null) this.setTurn(pos, 'CW');
        else if (current === 'CW') this.setTurn(pos, 'CCW');
        else this.setTurn(pos, null);
    }

    reset(): void {
        this.turns.clear();
        this.cachedPath = null;
        this.selectedDigit = null;
        this.selectedStart = false;
    }

    // ── Simulation ─────────────────────────────────────────────────

    /**
     * The cart's outgoing heading at `pos` given the heading it arrived with: apply the
     * cell's turn (a 90° rotation), then, while the heading faces a wall, reflect off it and
     * apply the turn again. On an empty cell that reduces to a single wall reflection — the
     * classic U-turn. This "keep turning past a wall" rule is what makes the drive reversible,
     * so it always closes back to the start (a turn that rotates into a wall bounces and turns
     * again rather than dead-ending). The 4-iteration cap only matters on a 1×1 grid.
     */
    protected resolveOut(pos: Position, incoming: Direction): { dir: Direction; uturn: boolean } {
        const turn = this.turns.get(positionKey(pos));
        let dir = incoming;
        let bounced = false;

        for (let i = 0; i < 4; i++) {
            if (turn) dir = turn === 'CW' ? turnCW(dir) : turnCCW(dir);
            if (isInside(this.grid.move(pos, dir))) break;
            dir = oppositeDir(dir);   // reflect head-on off the wall
            bounced = true;
            if (!turn) break;         // empty cell: a single reflection is the whole U-turn
        }
        return { dir, uturn: bounced };
    }

    /** Trace the whole drive from the fixed start until it loops (or gives up). */
    traceCart(): CartPath {
        if (this.cachedPath) return this.cachedPath;

        const steps: CartStep[] = [];
        const seen = new Set<string>();

        let pos: Position = { ...TurnsGame.START };
        let { dir, uturn } = this.resolveOut(pos, TurnsGame.START_DIR);
        const startKey = `${positionKey(pos)}:${dir}`;

        let loopClosed = false;
        const maxIterations = this.grid.size * this.grid.size * 4 + 4;
        let iterations = 0;

        while (iterations++ < maxIterations) {
            const key = `${positionKey(pos)}:${dir}`;
            if (seen.has(key)) {
                loopClosed = key === startKey;
                break;
            }
            seen.add(key);
            steps.push({ pos: { ...pos }, dir, uturn });

            const next = this.grid.move(pos, dir);
            if (!isInside(next)) break;  // ran into a wall unbounced — degenerate, no loop
            pos = next;
            ({ dir, uturn } = this.resolveOut(pos, dir));
        }

        this.cachedPath = this.summarize(steps, loopClosed);
        return this.cachedPath;
    }

    protected summarize(steps: CartStep[], loopClosed: boolean): CartPath {
        const passCounts = new Map<number, number>();
        const digitsHit: number[] = [];
        const clearOrder: number[] = [];
        const overPassed: number[] = [];

        for (const step of steps) {
            const digit = this.grid.getDigitAt(step.pos);
            if (digit === null) continue;
            // A wall bounce crosses the cell twice — in to the wall and back out — so a digit
            // on a bounce cell is driven over twice in that single visit.
            const crossings = step.uturn ? 2 : 1;
            const before = passCounts.get(digit) ?? 0;
            const after = before + crossings;
            passCounts.set(digit, after);
            if (before === 0) digitsHit.push(digit);
            if (before < 2 && after >= 2) clearOrder.push(digit);
            if (after > 2 && !overPassed.includes(digit)) overPassed.push(digit);
        }

        return { steps, loopClosed, digitsHit, passCounts, clearOrder, overPassed };
    }

    getPath(): CartPath {
        return this.traceCart();
    }

    /** Turns the cart never drives through — stray pieces that block the win (like Mirrors). */
    getUnusedTurns(): string[] {
        const visited = new Set<string>();
        for (const step of this.traceCart().steps) {
            visited.add(positionKey(step.pos));
        }
        return [...this.turns.keys()].filter(key => !visited.has(key));
    }

    /**
     * The drive clears every digit exactly on its second pass, in order — the win apart from
     * stray turns. Kept separate from checkWin so the status bar can distinguish "solved but
     * for an unused turn" from "not solved yet".
     */
    private driveClearsAllDigits(path: CartPath): boolean {
        if (!path.loopClosed) return false;

        const { minDigit, maxDigit, digitCount } = this.grid;

        // Every digit driven over exactly twice (not 0, 1, or 4+).
        for (let d = minDigit; d <= maxDigit; d++) {
            if (path.passCounts.get(d) !== 2) return false;
        }

        // Clears (second passes) happen in ascending digit order.
        if (path.clearOrder.length !== digitCount) return false;
        for (let i = 0; i < digitCount; i++) {
            if (path.clearOrder[i] !== minDigit + i) return false;
        }
        return true;
    }

    checkWin(): boolean {
        return this.driveClearsAllDigits(this.traceCart()) && this.getUnusedTurns().length === 0;
    }

    getScore(): ScoreBreakdown | null {
        if (!this.checkWin()) return null;

        const path = this.traceCart();
        const drivenCells = new Set<string>();
        for (const step of path.steps) {
            if (!this.grid.hasDigitAt(step.pos)) drivenCells.add(positionKey(step.pos));
        }
        return calculateScore(this.turns.size, drivenCells.size);
    }

    getScoringHighlight(): ScoringHighlight {
        const secondaryCells: Position[] = [];
        const seen = new Set<string>();
        for (const step of this.traceCart().steps) {
            const key = positionKey(step.pos);
            if (this.grid.hasDigitAt(step.pos) || seen.has(key)) continue;
            seen.add(key);
            secondaryCells.push({ ...step.pos });
        }
        return { secondaryCells };
    }

    getStatus(): string {
        const boldRed = (text: string) =>
            `<span style="color:#e94560;font-weight:bold">${text}</span>`;
        const boldAmber = (text: string) =>
            `<span style="color:#fbbf24;font-weight:bold">${text}</span>`;

        const path = this.traceCart();

        if (this.checkWin()) {
            return 'You win! The cart cleared every digit in order on its second pass!';
        }

        // Solved geometry, but stray turns off the path keep it from being a win.
        if (this.driveClearsAllDigits(path)) {
            const unused = this.getUnusedTurns().length;
            return `${boldAmber('Warning')}: ${unused} unused turn${unused > 1 ? 's' : ''} — remove ${unused > 1 ? 'them' : 'it'} to win.`;
        }

        if (!path.loopClosed) {
            return 'The cart does not loop back to the start yet.';
        }

        if (path.overPassed.length > 0) {
            const digits = [...path.overPassed].sort((a, b) => a - b).join(', ');
            return `${boldRed('Warning')}: the cart drives over digit${path.overPassed.length > 1 ? 's' : ''} ${digits} four times.`;
        }

        // A digit never reached, or only driven over once (never cleared).
        const { minDigit, maxDigit } = this.grid;
        for (let d = minDigit; d <= maxDigit; d++) {
            const count = path.passCounts.get(d) ?? 0;
            if (count < 2) {
                return count === 0
                    ? `Route the cart over every digit — digit ${d} is never reached.`
                    : `Digit ${d} is driven over once — it needs a second pass to clear.`;
            }
        }

        // All digits driven twice, but the clear order is wrong.
        for (let i = 0; i < path.clearOrder.length; i++) {
            if (path.clearOrder[i] !== minDigit + i) {
                return `${boldRed('Wrong order')}: cleared ${path.clearOrder[i]} but expected ${minDigit + i}.`;
            }
        }

        return 'Place turns to guide the cart over every digit twice, in order.';
    }

    // ── Serialization ──────────────────────────────────────────────

    /** Format: 3-char entries `<col><row><type>`, type c=CW a=CCW, e.g. "J0c". */
    serialize(): string {
        const entries: string[] = [];
        for (const [key, type] of this.turns) {
            const [row, col] = key.split(',').map(Number);
            const posStr = this.gs.serializePosition({ row, col });
            entries.push(`${posStr}${type === 'CW' ? 'c' : 'a'}`);
        }
        return entries.join('');
    }

    deserialize(serialized: string): void {
        this.reset();
        if (!serialized) return;

        for (const entry of splitEntries(serialized, 3)) {
            const posStr = entry.slice(0, 2);
            const typeChar = entry[2];
            try {
                const pos = this.gs.deserializePosition(posStr);
                this.setTurn(pos, typeChar === 'c' ? 'CW' : 'CCW');
            } catch {
                // Invalid entry, skip
            }
        }
    }
}
